// Search — Phase 6g.
//
// Takes a text query + 4 facet filters and returns matching conversations.
// Runs against public.messages.search_tsv (GIN index on body + sender_name),
// then fans out to the parent conversations with a single .in() query.
// Query key is stable across identical filter shapes so pagination and
// cache reuse work without juggling query strings.
//
// Shape of results matches enough of ConversationRow that reusing
// FollowupRow for the result list is trivial.

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Brand, Channel, ConversationStatus, Direction } from '@md/shared';

export type SearchHit = {
  id: string;
  channel: Channel;
  brand: Brand;
  subject: string | null;
  customer_email: string | null;
  status: ConversationStatus;
  read_at: string | null;
  last_message_at: string;
  last_message_direction: Direction | null;
  snoozed_until: string | null;
  status_changed_at: string;
  resolved_at: string | null;
  ai_summary: string | null;
  ai_summary_highlight: string | null;
};

export type SearchFilters = {
  /** Free-text query. Empty string means "all conversations, filters only." */
  q: string;
  /** undefined = no filter (all brands). */
  brand?: Brand;
  /** undefined = no filter (all channels). */
  channel?: Channel;
  /** undefined = no filter (all statuses). */
  status?: ConversationStatus;
  /**
   * Shortcut date buckets. "any" / "24h" / "7d" / "30d" — we translate to a
   * concrete last_message_at floor. Keeps the facet UI a simple 4-button row.
   */
  dateRange: 'any' | '24h' | '7d' | '30d';
};

const MS_PER_DAY = 86_400_000;

function dateFloor(range: SearchFilters['dateRange']): string | null {
  if (range === 'any') return null;
  const now = Date.now();
  if (range === '24h') return new Date(now - MS_PER_DAY).toISOString();
  if (range === '7d') return new Date(now - 7 * MS_PER_DAY).toISOString();
  if (range === '30d') return new Date(now - 30 * MS_PER_DAY).toISOString();
  return null;
}

export function useSearchConversations(filters: SearchFilters, enabled: boolean) {
  return useQuery<SearchHit[]>({
    queryKey: [
      'search',
      filters.q,
      filters.brand ?? 'any',
      filters.channel ?? 'any',
      filters.status ?? 'any',
      filters.dateRange,
    ],
    enabled,
    queryFn: async () => runSearch(filters),
    staleTime: 30_000,
  });
}

async function runSearch(filters: SearchFilters): Promise<SearchHit[]> {
  const { q } = filters;
  const trimmed = q.trim();
  const floor = dateFloor(filters.dateRange);

  // Path A — with text query: find matching conversation_ids via messages,
  // then load the parent conversation rows for rendering.
  // Path B — empty query: browse-style filter, hit conversations directly.

  let convIds: string[] | null = null;

  if (trimmed.length > 0) {
    // websearch_tsv syntax accepts phrases in quotes + AND/OR/- operators,
    // which is what Kimi's likely to type (e.g., `"proof change" -test`).
    const { data: msgRows, error: msgErr } = await supabase
      .from('messages')
      .select('conversation_id')
      .textSearch('search_tsv', trimmed, { type: 'websearch', config: 'english' })
      .limit(300);
    if (msgErr) throw msgErr;

    // Also search the conversations.subject column directly via ilike so
    // a short "proof" matches even if no message body contains it. This
    // makes empty-body test threads discoverable.
    const subjectTerm = trimmed.replace(/[%_]/g, '\\$&');
    const { data: subjRows, error: subjErr } = await supabase
      .from('conversations')
      .select('id')
      .ilike('subject', `%${subjectTerm}%`)
      .limit(100);
    if (subjErr) throw subjErr;

    const ids = new Set<string>();
    for (const m of msgRows ?? []) ids.add(m.conversation_id);
    for (const s of subjRows ?? []) ids.add(s.id);
    if (ids.size === 0) return [];
    convIds = [...ids];
  }

  let q2 = supabase
    .from('conversations')
    .select(
      'id, channel, brand, subject, customer_email, status, read_at, last_message_at, last_message_direction, snoozed_until, status_changed_at, resolved_at, ai_summary, ai_summary_highlight',
    )
    // Polish #4 — merged rows are ghosts; hide them from search too.
    .is('merged_into_id', null)
    .order('last_message_at', { ascending: false })
    .limit(100);

  if (convIds) q2 = q2.in('id', convIds);
  if (filters.brand) q2 = q2.eq('brand', filters.brand);
  if (filters.channel) q2 = q2.eq('channel', filters.channel);
  if (filters.status) q2 = q2.eq('status', filters.status);
  if (floor) q2 = q2.gte('last_message_at', floor);

  const { data, error } = await q2;
  if (error) throw error;
  return (data ?? []) as SearchHit[];
}
